import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Formik, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import { CatalogProduct } from '@/features/Payments/types'
import { createModal, useModalize } from '@/components/modalize/mui'
import { CloseIcon } from '@/components/icons'
import { fontWeights, fontSizes, colors, breakpoints } from '@/styles'
import { pushToDatalayer } from '@/utils/push-datalayer'
import PaymentForm from './payment-form'
import { showConfirmationModal } from './confirmation-modal'

type Props = {
  card: any
  mutation: any
  deletePaymentMethod: any
  client: any
  product?: CatalogProduct
  onSuccess?: () => void
}

type FormValues = {
  payment_method: 'stripe'
  default_payment_method: boolean
  first_name: string
  last_name: string
  street1: string
  street2?: string
  city: string
  state: string
  country: string
  zip: string
  card_number: string
  expiration_date: string
  security_code: string
  save_payment_method: boolean
  save_billing_address: boolean
  tax_code?: string
}

interface CustomWindow extends Window {
  dataLayer: Record<string, any>
}

declare let window: CustomWindow
// Styled Components
const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 700px;
  min-height: 600px;
  overflow-y: hidden;

  > :nth-child(2) {
    border-top: 1px solid ${colors.grey88};
    border-bottom: 1px solid ${colors.grey88};
    padding: 0 24px;
  }
  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    justify-content: space-between;
    min-width: 0;
  }
`
const Section = styled.div`
  display: flex;
  align-items: center;
  padding: 24px;
  justify-content: space-between;
`
const ModalTitle = styled.div`
  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.large};
`
const CloseButton = styled(CloseIcon)`
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  &:hover {
    opacity: 0.5;
  }
`

const EditPaymentMethod = ({
  card,
  mutation,
  deletePaymentMethod,
  client,
  product,
  onSuccess
}: Props) => {
  const { t } = useTranslation()
  const [deleting, setDeleting] = useState(false)
  const [loading, setLoading] = useState(false)
  const { close } = useModalize()

  const closeModal = () => {
    if (!loading) {
      close()
    }
  }

  const process = async (values: any) => {
    setLoading(true)
    mutation.mutate(
      { ...values, save_payment_method: true, default_payment_method: true },
      {
        onSuccess: async () => {
          client.invalidateQueries({ queryKey: ['checkoutPaymentMethod'] })
          pushToDatalayer({
            event: 'application-trust-score-submit-purchase'
          })

          onSuccess && (await onSuccess())
          close()
          setLoading(false)
        }
      }
    )
  }

  const onSubmit = async (
    values: any,
    { setSubmitting }: Pick<FormikHelpers<FormValues>, 'setSubmitting'>
  ) => {
    setSubmitting(true)
    showConfirmationModal({ process: process, values: values })
    setSubmitting(false)
  }

  const onDelete = async () => {
    setDeleting(true)
    deletePaymentMethod.mutate(card.payment_method_id, {
      onSuccess: async () => {
        client.invalidateQueries({ queryKey: ['checkoutPaymentMethod'] })
        close()
        setDeleting(false)
      },
      onError: () => {
        setDeleting(false)
      }
    })
  }

  const initialValues: FormValues = {
    default_payment_method: true,
    payment_method: 'stripe',
    first_name: card.first_name,
    last_name: card.last_name,
    street1: card.street1,
    street2: card.street2 ? card?.street2 : '',
    city: card.city,
    state: card.state,
    country: 'ca',
    zip: card.zip,
    card_number: '',
    expiration_date: card.expiration_date,
    security_code: '',
    save_payment_method: true,
    save_billing_address: true,
    tax_code: ''
  }
  const validationSchema = Yup.object().shape({
    first_name: Yup.string()
      .required()
      .matches(/^[aA-zZ\s]+$/, 'Can only contain letters'),
    last_name: Yup.string()
      .required()
      .matches(/^[aA-zZ\s]+$/, 'Can only contain letters'),
    street1: Yup.string().required(),
    street2: Yup.string(),
    city: Yup.string().required(),
    state: Yup.string().required(),
    country: Yup.string().required(),
    zip: Yup.string().required(),
    card_number: Yup.string().required(),
    expiration_date: Yup.string().length(4).required(),
    security_code: Yup.string().min(3).max(4).required(),
    default_payment_method: Yup.boolean(),
    save_payment_method: Yup.boolean(),
    save_billing_address: Yup.boolean()
  })

  return (
    <Wrapper>
      <Section>
        <ModalTitle>
          {t('l.plan_settings.edit.edit_payment_method.title', 'Edit Payment Method')}
        </ModalTitle>
        <CloseButton onClick={closeModal} width={16} height={16} />
      </Section>
      <Section>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ values, isValid, setFieldValue, setSubmitting }) => (
            <PaymentForm
              onSubmit={() => onSubmit(values, { setSubmitting })}
              onDelete={onDelete}
              values={values}
              setFieldValue={setFieldValue}
              isValid={isValid}
              card={card}
              edit={true}
              loading={loading}
              deleting={deleting}
              product={product}
            />
          )}
        </Formik>
      </Section>
    </Wrapper>
  )
}

export const showEditPaymentMethod = createModal(EditPaymentMethod, {
  width: 'auto',
  maxWidth: '700px',
  showClose: false,
  showBackground: false,
  clickOutside: false
})
